import { Badge } from "@bowfast/ui/components/badge";
import { Button } from "@bowfast/ui/components/button";
import { Card } from "@bowfast/ui/components/card";
import { Input } from "@bowfast/ui/components/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@bowfast/ui/components/tabs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { AnchorIcon, Globe2Icon, MapIcon, Navigation2Icon, PlusIcon, SearchIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import { EmptyRoutes } from "~/components/routes/EmptyRoutes";
import { FeaturedRoute } from "~/components/routes/FeaturedRoute";
import { RouteCard } from "~/components/routes/RouteCard";
import { RouteKPIStrip } from "~/components/routes/RouteKPIStrip";
import { ListLoadError } from "~/components/shared/ListLoadError";
import { fadeUp, stagger } from "~/lib/animations";
import { createSdk } from "~/lib/gql";
import { useOrg } from "~/lib/org-context";
import { buildRouteKPIs, toRouteCardRoute } from "./routes.utils";

// ── RoutesListPage ────────────────────────────────────────────────────────────

function TabContent({
  filtered,
  search,
}: Readonly<{ filtered: ReturnType<typeof Array.prototype.filter>; search: string }>) {
  if (filtered.length === 0 && search.trim()) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <SearchIcon className="size-8 text-muted-foreground/30" />
        <p className="text-sm text-muted-foreground">
          No routes match "<span className="font-medium text-foreground">{search}</span>"
        </p>
      </div>
    );
  }

  if (filtered.length === 0) {
    return <EmptyRoutes />;
  }

  return (
    <motion.div
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
      {...stagger}
      initial="initial"
      animate="animate"
    >
      {filtered.map((route) => (
        <motion.div key={route.id} variants={fadeUp}>
          <RouteCard route={route} />
        </motion.div>
      ))}
    </motion.div>
  );
}

export function RoutesListPage() {
  const auth = useAuth();
  const { orgId } = useOrg();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  const { data, isPending, isError } = useQuery({
    queryKey: ["routes-list", orgId],
    queryFn: () => createSdk(auth.user?.access_token ?? "").RoutesList({ orgId }),
    enabled: !!auth.user?.access_token && !!orgId,
    staleTime: 60 * 1000,
  });

  const rawRoutes = useMemo(() => data?.routes ?? [], [data]);
  const cardRoutes = useMemo(() => rawRoutes.map(toRouteCardRoute), [rawRoutes]);
  const kpis = useMemo(() => buildRouteKPIs(rawRoutes), [rawRoutes]);

  // Featured = longest route
  const featured = useMemo(() => [...cardRoutes].sort((a, b) => b.distance - a.distance)[0] ?? null, [cardRoutes]);
  const otherRoutes = useMemo(
    () => (featured ? cardRoutes.filter((r) => r.id !== featured.id) : cardRoutes),
    [cardRoutes, featured],
  );

  // Filter
  const filtered = useMemo(() => {
    let list = activeTab === "all" ? otherRoutes : otherRoutes.filter((r) => r.type === activeTab);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          r.departurePort.toLowerCase().includes(q) ||
          r.arrivalPort.toLowerCase().includes(q),
      );
    }
    return list;
  }, [otherRoutes, activeTab, search]);

  // Tab counts
  const counts = useMemo(
    () => ({
      all: otherRoutes.length,
      ocean: otherRoutes.filter((r) => r.type === "ocean").length,
      offshore: otherRoutes.filter((r) => r.type === "offshore").length,
      coastal: otherRoutes.filter((r) => r.type === "coastal").length,
    }),
    [otherRoutes],
  );

  const routeCountLabel = `${cardRoutes.length} ${cardRoutes.length === 1 ? "route" : "routes"} saved`;

  return (
    <div className="mx-auto w-full max-w-350 space-y-5 p-4 md:p-6">
      {/* ── Page header ── */}
      <motion.div {...fadeUp} className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex size-11 items-center justify-center rounded-xl bg-abyss-500 shadow-lg shadow-abyss-500/20">
            <MapIcon className="size-6 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-xl font-bold tracking-tight md:text-[1.375rem]">Route Library</h1>
            <p className="text-sm text-muted-foreground">{isPending ? "Loading…" : routeCountLabel}</p>
          </div>
        </div>
        <Button>
          <PlusIcon data-icon="inline-start" />
          Plot a Route
        </Button>
      </motion.div>

      {/* ── Error state ── */}
      {isError && (
        <ListLoadError
          title="Couldn’t load your routes"
          onRetry={() => queryClient.invalidateQueries({ queryKey: ["routes-list", orgId] })}
        />
      )}

      {/* ── KPI strip ── */}
      <RouteKPIStrip kpis={kpis} isPending={isPending} />

      {/* ── Featured route (longest) ── */}
      {!isPending && !isError && featured && <FeaturedRoute route={featured} />}

      {/* ── Filters + search + grid ── */}
      <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }}>
        <Card>
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as string)}>
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 pt-2 pb-0">
              <TabsList variant="line" className="gap-0">
                <TabsTrigger value="all">
                  All
                  <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-[0.625rem]">
                    {counts.all}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="ocean">
                  <Globe2Icon className="size-3 mr-1" />
                  Ocean
                  {counts.ocean > 0 && (
                    <Badge variant="active" className="ml-1 h-4 px-1.5 text-[0.625rem]">
                      {counts.ocean}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="offshore">
                  <Navigation2Icon className="size-3 mr-1" />
                  Offshore
                  {counts.offshore > 0 && (
                    <Badge variant="planning" className="ml-1 h-4 px-1.5 text-[0.625rem]">
                      {counts.offshore}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="coastal">
                  <AnchorIcon className="size-3 mr-1" />
                  Coastal
                  {counts.coastal > 0 && (
                    <Badge variant="open" className="ml-1 h-4 px-1.5 text-[0.625rem]">
                      {counts.coastal}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              <div className="relative">
                <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search routes…"
                  className="h-8 w-56 pl-8 text-sm"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            {["all", "ocean", "offshore", "coastal"].map((tab) => (
              <TabsContent key={tab} value={tab} className="px-5 pt-2 pb-5">
                <TabContent filtered={filtered} search={search} />
              </TabsContent>
            ))}
          </Tabs>
        </Card>
      </motion.div>
    </div>
  );
}
